import { useRouter } from 'next/router'

import { useTranslation } from 'next-i18next'

import { Link } from '~/components/Link'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { ActivityMenu, BootcampPageUrlParamsType, LevelActionType } from '~/bootcamp/types/bootcamp.types'
import { useAppSelector } from '~/redux/hooks'

import { selectBootcampPageLevelProgress } from '~/bootcamp/slices/bootcamp-page.slice'

import { checkIfLevelComplete } from '~/bootcamp/pages/BootcampView/BootcampView.utils'
import VideoCourseIcon, { QuizIcon } from '~/bootcamp/components/VideoCourseIcon'
import { TopicId, getPathGradientColors } from '~/dashboard/utils/learningPath.utils'

const ActivityPageMenu = ({ title, type, level, index, icon, onMenuClick }: ActivityMenu): React.ReactElement => {
  const { query } = useRouter()
  const { key: bootcampKey, moduleKey, subtopic } = query as unknown as BootcampPageUrlParamsType
  const color = getPathGradientColors(subtopic as TopicId)
  const levelProgress = useAppSelector(selectBootcampPageLevelProgress)

  let isSelected: boolean = level.toString() === query.level && type === query.action

  if (type === LevelActionType.Activity && isSelected) {
    isSelected = query.type === `${index}`
  }

  const pageUrl = `/bootcamp/${subtopic}/${bootcampKey}/${moduleKey}/${level}/${type}${
    type === LevelActionType.Activity ? `/${index}` : ''
  }`

  const isCompleted = checkIfLevelComplete(
    levelProgress?.processedLeafs,
    `${level}-${type}${type === LevelActionType.Activity ? `-${index}` : ''}`,
  )

  return (
    <Link
      className="flex gap-4 items-center hover:bg-gray-extra-light transition-colors"
      onClick={onMenuClick}
      replace
      scroll={false}
      shallow
      to={pageUrl}
    >
      {type === LevelActionType.Quiz ? (
        <QuizIcon isCompleted={isCompleted} />
      ) : (
        <VideoCourseIcon color={color} iconType={icon ?? type} size="x-small" />
      )}
      <div className="flex flex-1 items-center py-1 text-black-breadcrumb">{title}</div>
      <ActivityStatus isCompleted={isCompleted} />
    </Link>
  )
}

const mediaQueryGradient = [{ screen: '(min-width: 280px)', width: '16px' }]
const mediaQueryCheckIcon = [{ screen: '(min-width: 280px)', width: '18px' }]
const ActivityStatus = ({ isCompleted }: { isCompleted: boolean }): React.ReactElement => {
  const { t } = useTranslation('generics')
  return (
    <div className="w-5 relative">
      <ResponsiveImage
        alt={t('gradientColorCircle')}
        height={16}
        imageClassName="h-4"
        imageWidths="16, 20"
        mediaQuery={mediaQueryGradient}
        srcKey="icons/circle-purple-gradient.png"
        width={16}
      />
      {isCompleted && (
        <ResponsiveImage
          alt={t('purpleCheckIcon')}
          imageClassName="w-4.5 absolute -top-1 left-px"
          imageWidths="18"
          mediaQuery={mediaQueryCheckIcon}
          srcKey="icons/purple-check-icon.png"
          width={18}
        />
      )}
    </div>
  )
}

export default ActivityPageMenu
